import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from './client';
// Server: battle/SplashDroplet.java — one droplet of a killing blow's thrown blood. Every field is
// server-rolled; the client may report only WHERE it landed. `speedMul` is unit-free — pixel speed
// and gravity are client-space constants in fx/splash.ts.
//
// ALIASED rather than re-declared, deliberately: `resolveSplash` consumes these straight off the
// wire, so two hand-maintained copies of the same shape would be free to drift into a runtime
// mismatch the compiler would happily accept (both being structural).
import type { SplashDroplet, SplashHit } from '../fx/splash';
import { companionsKeys, type StainDto } from './companions';
import type { Outfit } from '../pet3d/types';
import { bagKeys } from './bag';
import { journalKeys } from './journal';
import { badgesKeys } from './badges';
import { walletKeys } from './wallet';
import { scheduleRewardRefetch } from './rewardRefetch';

// Server: battle/StatusDto.java — one status effect currently on an ActorDto.
export type StatusDto = { code: string; turnsLeft: number };

// Server: battle/KeeperDto.java — one human in a battle, named the way the rest of this game
// names a person (account id plus the unique display handle, Decision #10). Carried by
// BattleViewDto.readyKeeper / BattleTurnResultDto.readyKeeper so a client can render "Waiting for
// Wren" without a second round trip to resolve the id. Carries NO companion id, deliberately —
// ActorDto.companionId is the only field that ever does, and only for the viewer's own actors.
export type KeeperDto = { accountId: string; displayName: string };

// Server: battle/ParticipantDto.java — one row of the battle's roster strip (design spec
// 2026-09-12-coop-pve-design.md §3.2), projected from BattleParticipants.Participant joined to
// the account's display handle. `status` is the participant row's own active|left|done verbatim;
// `actorIndex` is the engine slot that keeper commands. `away` is the DERIVED "this keeper isn't
// really here" flag (status=="left", OR three missed 45s turn windows) a client renders a
// greyed-out slot for — deliberately one boolean rather than the raw timeout counter.
//
// `rating` (live-PvP seasons Task 13, opponent HUD): this participant's `arena_profiles.rating`,
// populated by the server ONLY when the battle `kind` is "arena_duel" — `null` for every other
// kind (solo, co-op, arena_attack, arena_practice), none of which carries a rating consequence a
// HUD needs to show. `BattleScreen`'s duel HUD line reads it off the OTHER participant (the one
// whose `accountId !== myId`).
export type ParticipantDto = {
  accountId: string; displayName: string; actorIndex: number; status: 'active' | 'left' | 'done'; away: boolean;
  rating: number | null;
};

// Server: battle/ReactionDto.java — one templated reaction posted into a live battle (design spec
// §3.8): a lightweight "hyping the fight" feed, not a chat message. `phraseCode` resolves against
// the `reaction` phrases container doc, never free text (no C3 moderation surface). `actorIndex`
// is the reacting keeper's own party slot. `atMs` is wall-clock epoch millis for client-side
// ordering/fade-out only — nothing server-side is ever compared against it.
export type ReactionDto = { actorIndex: number; phraseCode: string; atMs: number };

// Server: battle/AbilityStateDto.java — one ability an ActorDto knows. `cooldownLeft` was removed
// from the wire when cooldowns were deleted from the design (combat-rebuild Task 11) — mana is
// the game's only ability resource limiter now. `element`/`kind` (Phase 4 Task 16 widening,
// additive, optional — every existing AbilityStateDto fixture in this codebase predates it):
// `element` is the Element enum's name (e.g. "EMBER"), for the Abilities drawer's own badge;
// `kind` is one of physical/magical/support — `support` abilities resolve with no target prompt
// (TurnAction's own contract: target is ignored for a support ability), so ActionBar arms an
// enemy-targeting flow only for physical/magical. Optional/nullable rather than required for the
// same reason bag.ts's own slot/levelReq/effectType widenings are: nothing but the new battle
// screen reads them.
//
// `tempo`/`manaCost` (combat-rebuild Task 20 fix round — a coordinator-verified correction of an
// earlier note in this file that wrongly called these a TypeScript gap when they were actually
// missing from the server's own DTO): `tempo` is AbilityDef.tempo lower-cased to the same content
// code TurnAction.tempo already sends the other direction (e.g. "quick"/"strike"/"heavy" — NOT the
// Tempo enum's own upper-case name(), unlike `element`, which does use the upper-case Element
// name). `manaCost` is the ability's authored mana price. Both nullable for the exact same
// defensive-fallback reason element/kind already are (an ability code with no matching
// content-pack row).
//
// `targetsEnemy` (ability-slots-and-training-schools Task 12, fix for follow-up I-3): `true` only
// for a `kind: "support"` ability whose own content `effect.target` is `"enemy"` (e.g. `enfeeble`/
// `lullshade`/`undertow`) — everything else (self/ally supports, and every physical/magical
// ability, which already targets the enemy through the ordinary offensive-arming path below) gets
// `false`/`null`. Before this field existed, ActionBar could not distinguish an enemy-targeted
// support from a self/ally one and always sent the caster as the target, which the server 400s for
// an enemy-targeted support (`BattleEngine.resolveOpposingChoice` requires a living OPPOSING
// target). Read this, not a hardcoded ability-code list — "content is data, never code".
// `null`/`false` are handled identically (falls back to self-targeting), same defensive-fallback
// spirit as `manaCost`'s own null-is-free treatment just above.
export type AbilityStateDto = {
  code: string;
  name: string;
  element?: string | null;
  kind?: string | null;
  tempo?: string | null;
  manaCost?: number | null;
  targetsEnemy?: boolean | null;
};

// Server: battle/AtbEventDto.java — one ATB-state snapshot emitted by the engine during a turn
// resolution, for client-side playback. `kind` is "fill" (tick-jump snapshot) or "act" (actor
// acted, gauge reset to 0). `atb` is the actor's gauge value AFTER this event.
export type AtbEventDto = { kind: string; actorIndex: number; atb: number };

// Server: battle/ActorDto.java — player-facing projection of one ActorState, rebuilt fresh from
// the battle's current state_json on every read (never persisted separately). `element` is the
// Element enum's name (e.g. "EMBER"). `atb` is BattleMath.GAUGE_MIN..GAUGE_MAX — combat-rebuild
// Task 11 widened the range to -50..100 (a HEAVY action leaves the actor in gauge deficit; QUICK
// and STRIKE never go below 0). `controller` is "local" for the current account's commanded
// pets, "ai" for companions and enemies (ATB design doc §2). `auto` (combat-rebuild Task 18,
// §11.1) is true when a "local" party slot has been handed to BattleAi for the rest of the fight
// — always false for an "ai"-controlled actor, which has no player command to opt out of.
//
// `ward`/`mana`/`maxMana` (combat-rebuild Task 20 fix round — a coordinator-verified correction of
// an earlier note in this file that wrongly called these a TypeScript gap when they were actually
// missing from the server's own DTO): `ward` is the actor's stats.ward() — trained ward plus
// equipment bonuses, matching what every other stat here already means (NOT further adjusted by a
// live in-battle WRD_UP/WRD_DOWN status the way the engine's own internal effectiveWard()
// additionally does — no other field on this type is status-adjusted either). `mana`/`maxMana`
// are the actor's ability resource — the sole cast limiter now that cooldowns are gone.
//
// `controller`/`keeperName` (live battles, 2026-09-12 §3.1): `controller` is now one of exactly
// three wire tokens, rewritten PER VIEWER server-side (see the server ActorDto javadoc) —
// "local" (the viewer's own to command), "keeper" (another human's actor — `keeperName` carries
// their display handle, null in every other case), or "ai" (a PvE enemy or arena defender, nobody
// commands it). An account id is never projected here; a keeper is identified to other keepers by
// handle only. Solo and arena battles only ever produce "local"/"ai", exactly as before this
// widening — no existing fixture or call site that only ever used those two literals breaks.
export type ActorDto = {
  index: number;
  name: string;
  speciesCode: string;
  /** This companion's growth stage ("hatchling" / "juvenile" / "adult") — party actors only; null
   *  for a PvE enemy and for an arena defender (see the server `ActorDto` javadoc). Optional so
   *  fixtures and any cached pre-2026-09-07 payload stay valid; `BattleFighter` falls back to
   *  "adult", which is exactly what it hardcoded before this field existed. That hardcode was a
   *  real defect: every companion starts a hatchling, and a stain's x/y/scale are normalised to
   *  the sprite's whole square canvas, whose artwork sits differently per stage — so the same
   *  stain drew at a different size and on different anatomy here than on the habitat screen. */
  stage?: string | null;
  level: number;
  hp: number;
  maxHp: number;
  element: string;
  statuses: StatusDto[];
  abilities: AbilityStateDto[];
  defending: boolean;
  alive: boolean;
  atb: number;
  controller: 'local' | 'keeper' | 'ai';
  /** Another human's display handle, present only when `controller === 'keeper'`; null/absent
   *  otherwise (see the `controller`/`keeperName` doc comment above). Optional so every ActorDto
   *  fixture predating this field (which only ever used "local"/"ai") stays valid. */
  keeperName?: string | null;
  auto: boolean;
  ward: number;
  mana: number;
  maxMana: number;
  /** Server `BattleMath.atbGain` — gauge points gained per tick, `max(1, effectiveSpeed())` with
   *  HASTE/SLOW already folded in. Sent as the derived RATE rather than the raw `speed` stat so the
   *  turn timeline predicts the queue with the same number the engine advances it by; see the
   *  server-side `ActorDto` javadoc. Optional so fixtures and any pre-timeline cached payload stay
   *  valid — `turnQueue.ts` floors a missing value at 1. */
  atbGain?: number;
  // Task 9 (2026-08-23 battle-gore-and-wash): this actor's permanent battle stains — for a PARTY
  // actor, mirrors companions.ts's CompanionDto.stains for the same companion; always [] for an
  // ENEMY actor, which has no companionId and isn't a companion to stain.
  //
  // Served for EVERY party actor regardless of viewer (Task 15), unlike `companionId` below — so a
  // stain keeper A landed on keeper B's pet renders on both screens. Stains are already public on
  // a pet's profile, so there is nothing here to withhold.
  stains: StainDto[];
  /** W5: the worn outfit of a party actor, public like stains (a co-op friend's and a duel opponent's
   *  too); `{}` for enemies and for arena-practice / house-keeper opponents (no companion id). */
  outfit: Outfit;
  /**
   * Spec 2026-09-06 (Task 7): this actor's companion id. Present ONLY for the requesting account's
   * OWN party actors; always null/absent for the opposing side AND for another keeper's actor in a
   * co-op battle (a PvE enemy has none at all, and neither an arena defender's nor a friend's ids
   * are the viewer's to see — see the server-side `ActorDto` javadoc).
   *
   * **No longer what a splash report names** (Task 15, owner ruling 2026-09-13 — follow-up I-2):
   * `reportSplash` below addresses each landing by engine `index`, and the server maps index ->
   * companion from the battle's own persisted party. This field's per-viewer nature is precisely
   * WHY that changed — a client could only ever address its own pets, so a co-op kill could only
   * ever stain the submitting keeper's companion.
   *
   * Optional rather than required for the same reason `atbGain` above is: every ActorDto fixture
   * in this codebase predates it, and a cached payload from before this shipped is still valid.
   */
  companionId?: number | null;
};

// Server: battle/LogEntryDto.java — player-facing projection of one engine LogEntry. `defeated`
// folds the engine's synthetic "defeat" entries into the causing entry rather than emitting its
// own row (see the Java javadoc for the pairing rule).
export type LogEntryDto = {
  turnNo: number;
  actorIndex: number;
  type: string;
  targetIndex: number | null;
  abilityCode: string | null;
  itemCode: string | null;
  damage: number | null;
  heal: number | null;
  statusApplied: string | null;
  missed: boolean;
  defeated: boolean;
  message: string;
};

// Server: battle/BattleDropDto.java — one item granted by the terminal battle-reward drop roll. A
// currency-type result from that same roll is folded into RewardDto.shells instead of appearing
// here (see the Java javadoc's one documented corner case). `name`/`rarity` (Phase 4 Task 16
// widening, additive) let VictorySheet render a real drop row (icon + name + rarity label),
// mirroring world.ts's DropDto shape.
export type BattleDropDto = { itemCode: string; qty: number; name: string; rarity: string };

/** Server: battle/PetXpGainDto.java (Demo I). xpToNext[i] is the XP to leave level levelBefore+i. */
export type PetXpGainDto = {
  companionId: number; levelBefore: number; xpBefore: number; levelAfter: number; xpAfter: number;
  xpToNext: number[]; pointsGained: number;
};

// Server: battle/RewardDto.java — a terminal battle's granted rewards. null on a non-terminal
// battle, or a terminal one that granted nothing (lost/fled/expired — C1: no reward, no penalty).
//
// `dailyShells`/`thriving` (reward engine, 2026-09-28): the account's running battle-Shell total
// for the day against its cap, and whether this win carried the Thriving bonus. Both optional —
// an older finished battle cached before this shipped has neither key on the wire, and
// VictorySheet renders nothing for either in that case (not a false "not capped"/"not thriving").
export type RewardDto = {
  xp: number;
  shells: number;
  drops: BattleDropDto[];
  xpGains?: PetXpGainDto[];
  dailyShells?: { earnedToday: number; cap: number } | null;
  thriving?: boolean;
};

// `status` is one of active|won|lost|fled|expired (BattleService) — terminal iff status !== 'active'.
export type BattleStatus = 'active' | 'won' | 'lost' | 'fled' | 'expired';

export function isTerminalStatus(status: string): boolean {
  return status !== 'active';
}

// Server: battle/BattleViewDto.java — GET /battles/{id}, GET /battles/active, and the return of
// POST /battles. `log` is the FULL accumulated log across every resolved round (stamped per entry
// with the round it came from) — unlike BattleTurnResultDto.entries, which (ruling R1) carries only
// the just-submitted round's entries, so a resumed session can render full playback from this DTO
// alone. `ratingDelta` is always null for kind="pve"; arena.ts's kind="arena_attack" populates it.
// `readyActorIndex` is null when not `awaitingAction`, otherwise the actor whose gauge is full and
// needs player input (only "local" actors gate the dock). `atbEvents` not present on BattleViewDto
// (only on BattleTurnResultDto); treat missing as [].
//
// Live battles (design spec 2026-09-12-coop-pve-design.md §3.1/§3.2/§3.4): this DTO is built PER
// VIEWER, not per battle — two keepers reading the same `coop` row get different objects (see the
// server BattleViewDto/ActorDto javadoc for exactly what differs and why).
// - `participants` — the whole roster (§3.2), including keepers who have left. Present for every
//   kind: a solo or arena battle simply has exactly one entry, its owner.
// - `readyKeeper` — WHOSE turn it is, as a person rather than an actor index (§3.4), so a client
//   can render "Waiting for Wren" without resolving an id. `null` exactly when `readyActorIndex`
//   is: nobody is ready, i.e. the battle is terminal. For a solo battle this resolves to the
//   owner, which is true but uninteresting.
// - `turnDeadline` — the instant the ready keeper's 45s window expires (§3.4), or `null` when
//   nobody is ready. Informational only: the server enforces it (LiveTurnSweep), not the client.
// - `viewerSide` — `"party"` or `"enemies"`: which side of the fight the VIEWER's own participant
//   slot sits on. Always `"party"` for co-op and for every solo/arena battle; exists for the
//   live-PvP duel, where both sides are human.
// - `reactions` (§3.8) — the battle's own ReactionDto ring, at most 6 entries, oldest evicted
//   first. Shared across every viewer (unlike the per-viewer rewrites above).
// - `splashes` (§3.7) — one SplashManifestDto per kill row of the LATEST persisted round only
//   (not the whole log). Empty for every `arena_*` kind and for a battle with no persisted round.
export type BattleViewDto = {
  id: number;
  kind: string;
  encounterCode: string | null;
  status: BattleStatus;
  turnNo: number;
  party: ActorDto[];
  enemies: ActorDto[];
  log: LogEntryDto[];
  awaitingAction: boolean;
  reward: RewardDto | null;
  ratingDelta: number | null;
  readyActorIndex: number | null;
  participants: ParticipantDto[];
  readyKeeper: KeeperDto | null;
  turnDeadline: string | null;
  viewerSide: 'party' | 'enemies';
  reactions: ReactionDto[];
  splashes: SplashManifestDto[];
};

// Server: battle/BattleTurnResultDto.java — return of POST /battles/{id}/turns. `entries` carries
// ONLY the just-submitted round's log rows (ruling R1) — the client already has everything before
// this round. `party`/`enemies` are the CURRENT post-round snapshots. On an idempotent same-turnNo
// retry, this is rebuilt from the persisted round rather than re-resolved (safe to retry).
// `readyActorIndex` mirrors BattleViewDto's field. `atbEvents` is an ordered list of per-actor ATB
// snapshots (fill tick-jumps + act resets) for playback animation; treat missing as [].
export type BattleTurnResultDto = {
  battleId: number;
  turnNo: number;
  entries: LogEntryDto[];
  status: BattleStatus;
  awaitingAction: boolean;
  party: ActorDto[];
  enemies: ActorDto[];
  reward: RewardDto | null;
  ratingDelta: number | null;
  readyActorIndex: number | null;
  atbEvents?: AtbEventDto[];
  /**
   * Server: `BattleTurnResultDto.splashes` (spec 2026-09-06 §3.1) — one manifest per PvE killing
   * blow this round produced. Optional here for the same reason `atbEvents` is: treat missing as
   * `[]`. Empty on an idempotent same-turnNo retry (the round payload does not store it), and
   * always empty for a non-PvE battle (D2).
   */
  splashes?: SplashManifestDto[];
  /**
   * Server: `BattleTurnResultDto.readyKeeper`/`.turnDeadline` (live battles, 2026-09-12 §3.4) —
   * present on the Java record even though the task-10 brief's own interface sketch only listed
   * these two fields on `BattleViewDto`; the Java record is the source of truth (see this file's
   * own header note and task-10-report.md), so both are carried here too, with the SAME "answer
   * whose turn now, and until when" contract `BattleViewDto` documents above: a co-op client can
   * hand the screen over to the waiting state right off a turn result, with no follow-up GET.
   * Required (never optional) — the server always includes them on every BattleTurnResultDto,
   * unlike `atbEvents`/`splashes` above, which are genuinely absent on an idempotent retry.
   */
  readyKeeper: KeeperDto | null;
  turnDeadline: string | null;
};

// Server: battle/SplashDroplet.java — see the aliasing note on the import at the top of this file.
export type SplashDropletDto = SplashDroplet;

// Server: battle/SplashManifestDto.java — one killing blow's blood, addressed by
// (turnNo, logIndex). `victimIndex` is the dying enemy, i.e. where the burst originates.
//
// `logIndex` is the row's index within ITS OWN ROUND's mapped log (`BattleService.rollSplashes`
// walks `mapLog(rounds.size(), result.entries())`, and `requireIssuedManifest` re-derives it the
// same way), NOT an index into the client's full accumulated `BattleViewDto.log`. A client holding
// the whole log must convert before matching or reporting.
//
// `fluid` (3D characters battle, spec §5): the ids-8..15 fluid slots the 3D floor throws in addition
// to the eight droplets. Absent on a payload from before the server rolled them.
export type FluidSlotDto = { id: number; seed: number; scale: number; rot: number; tone: number };
export type SplashManifestDto = {
  turnNo: number; logIndex: number; victimIndex: number; droplets: SplashDropletDto[];
  fluid?: FluidSlotDto[];
};

// POST /battles/{id}/splash — BattleController.reportSplash. 204, no body.
//
// Hits are addressed by ENGINE ACTOR INDEX (Task 15, owner ruling 2026-09-13 — follow-up I-2), so
// a co-op keeper can report a droplet that landed on a FRIEND's pet without ever being handed that
// friend's companion id. The server resolves index -> companion from the battle's own party.
//
// Cosmetic by nature: a rejected or failed report simply leaves that kill with no stains (S7 — the
// server has no random fallback), so every caller must swallow the failure rather than surface it.
export function reportSplash(input: {
  battleId: number; turnNo: number; logIndex: number; hits: SplashHit[];
  /** The request may outlive the page (a flush on pagehide/unmount). */
  keepalive?: boolean;
}): Promise<null> {
  return apiFetch<null>(`/battles/${input.battleId}/splash`, {
    method: 'POST',
    body: JSON.stringify({ turnNo: input.turnNo, logIndex: input.logIndex, hits: input.hits }),
    ...(input.keepalive ? { keepalive: true } : {}),
  });
}

// Server: battle/TurnAction.java — one actor's requested action for its slot in the round.
// type "attack": targetIndex required. "ability": abilityCode required, targetIndex required for
// damage abilities. "item": itemCode + targetIndex (same side) required. "defend"/"flee": no
// target.
//
// `tempo` (combat-rebuild Task 22 — a real gap this task's own scope closed: the server record
// already carries a `tempo` field, added alongside the `Tempo` enum, but this client-side type
// never picked it up until now, so ActionBar had no way to ask for anything but STRIKE). Read for
// type="attack" only, one of "quick"/"strike"/"heavy" (`Tempo.fromCode`'s own lower-case codes,
// same convention `AbilityStateDto.tempo` already established the other direction); ignored by
// the server for every other type. Optional/nullable rather than required — `Tempo.fromCode(null)`
// already defaults to STRIKE server-side, and most existing TurnAction literals across the
// codebase predate this field and have no tempo to give.
export type TurnAction = {
  type: 'attack' | 'ability' | 'item' | 'defend' | 'flee';
  abilityCode: string | null;
  itemCode: string | null;
  targetIndex: number | null;
  tempo?: 'quick' | 'strike' | 'heavy' | null;
};

// Server: battle/EncounterListItemDto.java — one row of GET /battles/encounters?activity=<code>,
// ordered easiest-first by the server (tier, then recommendedLevel, then boss last, then code) —
// NOT the activity's config.encounters[] authoring order, which is not kept sorted. Render as
// given; do not re-sort. `unmet` is the first unmet requirement type code,
// or null if every requirement is satisfied (RequirementService.firstUnmet, companionId=null —
// falls back to the account's highest-level companion; no specific party chosen yet at this
// venue-browsing stage).
export type EncounterListItemDto = {
  code: string;
  name: string;
  tier: number;
  boss: boolean;
  recommendedLevel: number;
  unmet: string | null;
};

export const battleKeys = {
  all: ['battles'] as const,
  active: ['battles', 'active'] as const,
  byId: (id: number) => ['battles', id] as const,
  encounters: (activityCode: string) => ['battles', 'encounters', activityCode] as const,
};

// GET /battles/active — 200 with an EMPTY body when the account has no active battle (QA
// 2026-09-23: it used to 404 `not_found`, a console error on every land/venue/arena load), which
// apiFetch resolves to `null`. useActiveBattle below still folds the old 404 to `null` too, for
// any server still serving it and for the unit tests that mock it — same "expected absent state,
// not a query error" convention as keeper.ts's useKeeper.
export const getActiveBattle = () => apiFetch<BattleViewDto | null>('/battles/active');

/** `options.enabled` — an optional passthrough (undefined by every pre-existing call site, which
 *  behaves exactly as before) so a caller that needs this read in only ONE branch does not fire it
 *  on every mount. `LobbySheet` is that caller: it asks "did the host already start?" only once its
 *  own lobby row has genuinely vanished, never while the lobby is still open. */
export function useActiveBattle(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: battleKeys.active,
    enabled: options?.enabled,
    queryFn: async (): Promise<BattleViewDto | null> => {
      try {
        return await getActiveBattle();
      } catch (err) {
        if (err instanceof ApiClientError && err.code === 'not_found') return null;
        throw err;
      }
    },
  });
}

export const getBattle = (id: number) => apiFetch<BattleViewDto>(`/battles/${id}`);

// `options.refetchInterval` (live battles, 2026-09-12 Q7) — copied verbatim from auctions.ts's own
// useAuction widening: an optional passthrough (undefined by every pre-existing call site, which
// behaves exactly as before) so a co-op battle screen can drive `useLivePolling`'s `2000 | false`
// straight into this hook while it is genuinely someone else's turn, with zero polling the moment
// it is the viewer's own turn again or the battle goes terminal.
export function useBattle(id: number | undefined, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: battleKeys.byId(id ?? -1),
    queryFn: () => getBattle(id as number),
    enabled: id != null,
    refetchInterval: options?.refetchInterval,
  });
}

// POST /battles/{id}/leave — BattleController.leave (live battles, §3.5): a guest keeper walks
// out of a co-op battle (their slot flips to Auto, per BattleAi) or, for a solo/arena battle,
// this is simply unreachable from the UI (BattleScreen never offers Leave outside co-op) — the
// server itself decides what "leaving" means for the battle's own kind. No body, 204/empty
// response.
export const leaveBattle = (id: number) => apiFetch<null>(`/battles/${id}/leave`, { method: 'POST' });

// POST /battles/{id}/reactions {phraseCode} — BattleController.react (§3.8, Q8): posts one
// templated reaction phrase into the battle's shared ring (BattleViewDto.reactions), rate-limited
// server-side (10/min/account) and rejected for any phraseCode that doesn't resolve against the
// `reaction` phrases container doc — never free text, so this carries no C3 moderation surface.
// No body beyond phraseCode, 204/empty response.
export const react = (id: number, phraseCode: string) =>
  apiFetch<null>(`/battles/${id}/reactions`, { method: 'POST', body: JSON.stringify({ phraseCode }) });

export const getEncounters = (activityCode: string) =>
  apiFetch<EncounterListItemDto[]>(`/battles/encounters?activity=${encodeURIComponent(activityCode)}`);

export function useEncounters(activityCode: string | undefined) {
  return useQuery({
    queryKey: battleKeys.encounters(activityCode ?? ''),
    queryFn: () => getEncounters(activityCode as string),
    enabled: activityCode != null,
  });
}

// POST /battles body {activityCode, encounterCode, partyCompanionIds} — BattleController.start.
export type StartBattleInput = {
  activityCode: string;
  encounterCode: string;
  partyCompanionIds: number[];
};

export const startBattle = (input: StartBattleInput) =>
  apiFetch<BattleViewDto>('/battles', { method: 'POST', body: JSON.stringify(input) });

// A typed result rather than a throw for the two expected, client-actionable refusals — the same
// "surface as data, not toast" contract the brief asks useSubmitTurn to follow, applied here too
// since start() shares the exact same battle_active 409 (BattleController.BattleActiveBody) that
// arena.ts's useAttack also needs to resolve into a "Resume" affordance. Any OTHER failure
// (party_invalid, encounter_unknown, network, etc.) still throws normally — screens that don't
// care about these two cases can just call mutateAsync/await and let the existing generic error
// handling catch it.
export type StartBattleResult =
  | { ok: true; battle: BattleViewDto }
  | { ok: false; code: 'battle_active'; activeBattleId: number; message: string }
  | { ok: false; code: 'requirement_unmet'; firstUnmet: string; message: string };

async function startBattleTyped(input: StartBattleInput): Promise<StartBattleResult> {
  try {
    const battle = await startBattle(input);
    return { ok: true, battle };
  } catch (err) {
    if (err instanceof ApiClientError && err.code === 'battle_active') {
      const activeBattleId = Number(err.details?.activeBattleId ?? 0);
      return { ok: false, code: 'battle_active', activeBattleId, message: err.message };
    }
    if (err instanceof ApiClientError && err.code === 'requirement_unmet') {
      const firstUnmet = String(err.details?.firstUnmet ?? '');
      return { ok: false, code: 'requirement_unmet', firstUnmet, message: err.message };
    }
    throw err;
  }
}

/**
 * NOT optimistic — the server owns whether the party/encounter/requirements are actually valid.
 * Resolves (never rejects) on the two refusals a screen can act on directly: `battle_active` (409,
 * carries `activeBattleId` off `ApiClientError.details` — see client.ts's Task 14 widening — so the
 * caller can offer "Resume" instead of a dead-end toast) and `requirement_unmet` (403, carries
 * `firstUnmet`). Every other failure (party_invalid, encounter_unknown, network) still rejects
 * normally. On success, seeds battleKeys.byId(battle.id) directly from the response (avoids a
 * redundant GET the screen would otherwise issue immediately to render the just-started battle) and
 * invalidates battleKeys.active (GET /battles/active now resolves to this battle).
 */
export function useStartBattle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: startBattleTyped,
    onSuccess: (result) => {
      if (result.ok) {
        queryClient.setQueryData(battleKeys.byId(result.battle.id), result.battle);
        queryClient.invalidateQueries({ queryKey: battleKeys.active });
      }
    },
  });
}

// POST /battles/{id}/turns body {turnNo, action} — BattleController.submitTurn.
export type SubmitTurnInput = { battleId: number; turnNo: number; action: TurnAction };

export const submitTurn = (input: SubmitTurnInput) =>
  apiFetch<BattleTurnResultDto>(`/battles/${input.battleId}/turns`, {
    method: 'POST',
    body: JSON.stringify({ turnNo: input.turnNo, action: input.action }),
  });

// Typed result, not a throw-to-toast — a 400 illegal_turn (stale/skipped turnNo, dead target,
// ability on cooldown, etc. — IllegalTurnException's own javadoc lists every trigger) is a real,
// EXPECTED rejection a battle screen must recover from in place (re-sync from the current
// BattleViewDto and let the player pick again), not a generic error banner.
export type SubmitTurnResult =
  | { ok: true; result: BattleTurnResultDto }
  | { ok: false; code: 'illegal_turn'; message: string };

async function submitTurnTyped(input: SubmitTurnInput): Promise<SubmitTurnResult> {
  try {
    const result = await submitTurn(input);
    return { ok: true, result };
  } catch (err) {
    if (err instanceof ApiClientError && err.code === 'illegal_turn') {
      return { ok: false, code: 'illegal_turn', message: err.message };
    }
    throw err;
  }
}

/**
 * Always invalidates battleKeys.byId(battleId) — the round may have resolved (both the ok and the
 * illegal_turn branches can follow a state change server-side is authoritative on either way; safe
 * and cheap to just refetch). On a TERMINAL round (result.status !== 'active') also invalidates
 * companions (XP/level-ups — BattleService grants full rewards_json.xp to every party companion via
 * XpService.grantTo), bag (item drops), wallet (RewardDto.shells — the codebase's own established
 * "any mutation that can move Shells must invalidate walletKeys" lesson, see world.ts's useForage),
 * journal (a won battle can complete a daily/weekly/quest step), and badges (a battle-triggered
 * badge award). Arena-kind terminal battles ALSO affect arena rating, but that invalidation is
 * arena.ts's useAttack's job (submitTurn is never called directly against an arena_attack battle
 * from outside that flow in this task's scope) — not duplicated here.
 */
export function useSubmitTurn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: submitTurnTyped,
    onSuccess: (typedResult, variables) => {
      queryClient.invalidateQueries({ queryKey: battleKeys.byId(variables.battleId) });
      if (typedResult.ok && isTerminalStatus(typedResult.result.status)) {
        queryClient.invalidateQueries({ queryKey: companionsKeys.all });
        queryClient.invalidateQueries({ queryKey: bagKeys.all });
        queryClient.invalidateQueries({ queryKey: walletKeys.all });
        queryClient.invalidateQueries({ queryKey: journalKeys.all });
        queryClient.invalidateQueries({ queryKey: badgesKeys.all });
        // A won battle can complete a daily/quest step, but that reward lands via the outbox
        // AFTER this response — the journal invalidation above is too early to see it.
        scheduleRewardRefetch(queryClient);
      }
    },
  });
}

// POST /battles/{id}/auto body {actorIndex, auto} — BattleController.setAuto (combat-rebuild
// Task 18). Hands a companion slot (party index 1 or 2 — never slot 0, the lead: BattleService's
// own `setAuto` refuses that with `illegal_turn`, and Task 23's UI never even offers the control
// for slot 0) to BattleAi for the rest of the fight, or takes it back. Server always accepts this
// regardless of whose turn it currently is — this is a standing per-slot preference, not itself a
// turn action.
export type SetAutoInput = { battleId: number; actorIndex: number; auto: boolean };

export const setAuto = (input: SetAutoInput) =>
  apiFetch<BattleViewDto>(`/battles/${input.battleId}/auto`, {
    method: 'POST',
    body: JSON.stringify({ actorIndex: input.actorIndex, auto: input.auto }),
  });

/**
 * Response is the refreshed `BattleViewDto` (same shape `GET /{id}` returns), so this seeds
 * `battleKeys.byId` directly off the mutation result rather than invalidating-and-refetching —
 * same "avoid a redundant round-trip when the mutation response already IS the fresh view" idiom
 * `useStartBattle` established.
 */
export function useSetAuto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setAuto,
    onSuccess: (view) => {
      queryClient.setQueryData(battleKeys.byId(view.id), view);
    },
  });
}
